import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { GrnMetricsSummary } from '@/types/domain/grn-management';
import {
  PackageCheck,
  Clock,
  AlertTriangle,
  TrendingUp,
  ShieldCheck,
  Truck,
} from 'lucide-react';

interface GrnKpiCardsProps {
  metrics: GrnMetricsSummary;
  onSelectFilter?: (filterType: string, value: string) => void;
}

export const GrnKpiCards: React.FC<GrnKpiCardsProps> = ({ metrics, onSelectFilter }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5 font-poppins">
      {/* Card 1: Open Deliveries */}
      <Card
        onClick={() => onSelectFilter?.('status', 'ALL')}
        className="rounded-2xl border-border bg-card hover:border-primary/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-foreground-secondary">
              Open Deliveries
            </span>
            <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-600 group-hover:scale-110 transition-transform">
              <Truck className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-foreground">{metrics.openDeliveries}</span>
            <span className="text-[10px] text-foreground-muted font-bold">in transit</span>
          </div>
        </CardContent>
      </Card>

      {/* Card 2: Due Today */}
      <Card
        onClick={() => onSelectFilter?.('status', 'DUE_TODAY')}
        className="rounded-2xl border-border bg-card hover:border-amber-500/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-amber-600">
              Due Today
            </span>
            <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-600 group-hover:scale-110 transition-transform">
              <Clock className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-amber-600">{metrics.dueToday}</span>
            <span className="text-[10px] text-amber-600/80 font-bold">expected dock</span>
          </div>
        </CardContent>
      </Card>

      {/* Card 3: Overdue Deliveries */}
      <Card
        onClick={() => onSelectFilter?.('status', 'OVERDUE')}
        className="rounded-2xl border-border bg-card hover:border-rose-500/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-rose-600">
              Overdue / Delayed
            </span>
            <div className="p-1.5 rounded-lg bg-rose-500/10 text-rose-600 group-hover:scale-110 transition-transform">
              <AlertTriangle className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-rose-600">{metrics.overdueDeliveries}</span>
            <span className="text-[10px] text-rose-600/80 font-bold">past promised</span>
          </div>
        </CardContent>
      </Card>

      {/* Card 4: On-Time Delivery Rate */}
      <Card className="rounded-2xl border-border bg-card shadow-2xs">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-emerald-600">
              On-Time Rate
            </span>
            <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600">
              <TrendingUp className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-emerald-600">{metrics.onTimeDeliveryRate}%</span>
            <span className="text-[10px] text-emerald-600/80 font-bold">SLA target: 90%</span>
          </div>
        </CardContent>
      </Card>

      {/* Card 5: Quality Acceptance Rate */}
      <Card
        onClick={() => onSelectFilter?.('qualityStatus', 'PASSED')}
        className="rounded-2xl border-border bg-card hover:border-emerald-500/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-primary">
              Quality Acceptance
            </span>
            <div className="p-1.5 rounded-lg bg-primary/10 text-primary group-hover:scale-110 transition-transform">
              <ShieldCheck className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-primary">{metrics.qualityAcceptanceRate}%</span>
            <span className="text-[10px] text-foreground-muted font-bold font-mono">
              Rej: {metrics.rejectionRate}%
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Card 6: Total GRNs Posted */}
      <Card className="rounded-2xl border-border bg-card shadow-2xs">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-foreground-secondary">
              GRNs Created
            </span>
            <div className="p-1.5 rounded-lg bg-purple-500/10 text-purple-600">
              <PackageCheck className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-purple-700">{metrics.totalGrns}</span>
            <span className="text-[10px] text-purple-600/80 font-bold">
              SAP: {metrics.sapSyncedGrnCount}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
